import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { AccountType, FormErrors, RegisterFormState, RegisterContextProps } from '../types';
import { validateEmail, validatePassword, validateCpf, validateCnpj, validatePhone } from '../utils/validation';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';

// Create context
const RegisterFormContext = createContext<RegisterContextProps | undefined>(undefined);

// Initial form state
const initialFormState: RegisterFormState = {
  fullName: '',
  email: '',
  accountType: '',
  address: '',
  city: '',
  state: '',
  phone: '',
  cpf: '',
  cnpj: '',
  password: '',
  confirmPassword: '',
};

// Provider component
export const RegisterFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [formState, setFormState] = useState<RegisterFormState>(initialFormState);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigate('/'); // Redirect if already logged in
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormState(prev => ({ ...prev, [id]: value }));
  };

  // Account type selector
  const handleAccountTypeSelect = (type: AccountType) => {
    setFormState(prev => ({ ...prev, accountType: type }));
  };

  // Input masking functions
  const applyPhoneMask = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    
    if (numericValue.length <= 10) {
      return numericValue.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, p1, p2, p3) => {
        let result = '';
        if (p1) result += `(${p1}`;
        if (p2) result += `) ${p2}`;
        if (p3) result += `-${p3}`;
        return result;
      });
    } else {
      return numericValue.replace(/(\d{2})(\d{0,5})(\d{0,4})/, (_, p1, p2, p3) => {
        let result = '';
        if (p1) result += `(${p1}`;
        if (p2) result += `) ${p2}`;
        if (p3) result += `-${p3}`;
        return result;
      });
    }
  };

  const applyCpfMask = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    
    return numericValue.replace(/(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})/, (_, p1, p2, p3, p4) => {
      let result = '';
      if (p1) result += p1;
      if (p2) result += `.${p2}`;
      if (p3) result += `.${p3}`;
      if (p4) result += `-${p4}`;
      return result;
    });
  };

  const applyCnpjMask = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    
    return numericValue.replace(/(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})/, (_, p1, p2, p3, p4, p5) => {
      let result = '';
      if (p1) result += p1;
      if (p2) result += `.${p2}`;
      if (p3) result += `.${p3}`;
      if (p4) result += `/${p4}`;
      if (p5) result += `-${p5}`;
      return result;
    });
  };

  // Masked input handlers
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = applyPhoneMask(e.target.value);
    setFormState(prev => ({ ...prev, phone: maskedValue }));
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = applyCpfMask(e.target.value);
    setFormState(prev => ({ ...prev, cpf: maskedValue }));
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = applyCnpjMask(e.target.value);
    setFormState(prev => ({ ...prev, cnpj: maskedValue }));
  };

  // Step validation functions
  const validateStepOne = () => {
    const newErrors: FormErrors = {};
    
    const sanitizedFullName = DOMPurify.sanitize(formState.fullName.trim());
    if (!sanitizedFullName) {
      newErrors.fullName = 'Nome completo é obrigatório';
    }
    
    if (!formState.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(formState.email)) {
      newErrors.email = 'Email inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStepTwo = () => {
    const newErrors: FormErrors = {};
    
    if (!formState.accountType) {
      newErrors.accountType = 'Selecione um tipo de conta';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStepThree = () => {
    const newErrors: FormErrors = {};
    
    if (formState.accountType === 'PF') {
      if (formState.cpf && !validateCpf(formState.cpf)) {
        newErrors.cpf = 'CPF inválido';
      }
    } else if (formState.accountType === 'PJ') {
      if (formState.cnpj && !validateCnpj(formState.cnpj)) {
        newErrors.cnpj = 'CNPJ inválido';
      }
    }
    
    if (formState.phone && !validatePhone(formState.phone)) {
      newErrors.phone = 'Telefone inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStepFour = () => {
    const newErrors: FormErrors = {};
    
    if (!formState.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (!validatePassword(formState.password)) {
      newErrors.password = 'A senha deve ter pelo menos 8 caracteres, uma letra maiúscula, uma minúscula e um número';
    }
    
    if (formState.password !== formState.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step navigation
  const goToNextStep = () => {
    let isValid = false;
    
    switch (currentStep) {
      case 1:
        isValid = validateStepOne();
        break;
      case 2:
        isValid = validateStepTwo();
        break;
      case 3:
        isValid = validateStepThree();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isLoading || isSubmitting) return;
    
    // Clear previous errors
    setErrors({});
    
    // Validate data of the final step
    if (!validateStepFour()) {
      return;
    }
    
    setIsLoading(true);
    setIsSubmitting(true);
    
    try {
      console.log('Starting registration process...');
      
      // Check if email is already in use (case-insensitive)
      const { data: emailCheckData, error: emailCheckError } = await supabase
        .from('user_profiles')
        .select('email')
        .ilike('email', formState.email)
        .maybeSingle();
        
      if (emailCheckError) {
        console.error('Email check error:', emailCheckError);
        throw emailCheckError;
      }
      
      if (emailCheckData) {
        console.log('Email already in use:', emailCheckData);
        setErrors({ email: 'Este email já está em uso' });
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Email check passed, registering user...');
      console.log('Form state:', {
        email: formState.email,
        fullName: formState.fullName,
        accountType: formState.accountType,
        address: formState.address,
        city: formState.city,
        state: formState.state,
        phone: formState.phone,
        cpf: formState.cpf,
        cnpj: formState.cnpj
      });
      
      // Register user with Supabase Auth
      // This will trigger handle_new_user function that creates a basic profile
      const { data, error } = await supabase.auth.signUp({
        email: formState.email,
        password: formState.password,
        options: {
          data: {
            full_name: DOMPurify.sanitize(formState.fullName.trim())
          }
        }
      });
      
      if (error) {
        console.error('User registration error:', error);
        throw error;
      }
      
      console.log('User registered successfully:', data);

      // Check if the user object exists
      if (!data.user) {
        console.error('No user object returned from signUp!');
        throw new Error('Falha ao criar usuário. Por favor, tente novamente.');
      }
      
      console.log('User object:', data.user);
      console.log('User ID:', data.user.id);

      try {
        // Small delay to ensure the trigger function has time to create the basic profile
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Prepare the additional profile data
        const additionalProfileData = {
          tipo_conta: formState.accountType || '',
          endereco: formState.address || '',
          cidade: formState.city || '',
          estado: formState.state || '',
          telefone: formState.phone || '',
          cpf: formState.accountType === 'PF' ? (formState.cpf || '') : '',
          cnpj: formState.accountType === 'PJ' ? (formState.cnpj || '') : ''
        };

        console.log('Updating profile with additional data:', additionalProfileData);

        // Update the profile with additional information
        const { data: updateResult, error: updateError } = await supabase
          .from('user_profiles')
          .update(additionalProfileData)
          .eq('user_id', data.user.id)
          .select();
          
        if (updateError) {
          console.error('Profile update error:', updateError);
          throw updateError;
        }

        console.log('Profile updated successfully:', updateResult);
      } catch (profileError) {
        console.error('Error during profile update:', profileError);
        throw profileError;
      }
      
      // Show success message
      Swal.fire({
        title: 'Cadastro realizado com sucesso!',
        text: 'Você será redirecionado para a página de login.',
        icon: 'success',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'btn-primary'
        }
      }).then(() => {
        navigate('/login');
      });
      
    } catch (error: any) {
      console.error('Erro ao registrar usuário:', error);
      
      Swal.fire({
        title: 'Erro!',
        text: error.message || 'Ocorreu um erro ao cadastrar. Por favor, tente novamente.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  // Context value
  const contextValue: RegisterContextProps = {
    formState,
    setFormState,
    currentStep,
    totalSteps,
    goToNextStep,
    goToPreviousStep,
    errors,
    handleInputChange,
    handleAccountTypeSelect,
    applyPhoneMask,
    applyCpfMask,
    applyCnpjMask,
    handlePhoneChange,
    handleCpfChange,
    handleCnpjChange,
    isLoading,
    isSubmitting,
    handleSubmit,
  };

  return (
    <RegisterFormContext.Provider value={contextValue}>
      {children}
    </RegisterFormContext.Provider>
  );
};

// Custom hook to use the register form context
export const useRegisterForm = () => {
  const context = useContext(RegisterFormContext);
  
  if (context === undefined) {
    throw new Error('useRegisterForm must be used within a RegisterFormProvider');
  }
  
  return context;
};

// Standalone hook for components outside the provider
export const useRegisterFormStandalone = () => {
  const [formState, setFormState] = useState<RegisterFormState>(initialFormState);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Similar implementation as above, but for standalone use
  // This is a simplified version for the default export
  
  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Just show an error since this is a standalone version
    Swal.fire({
      title: 'Erro!',
      text: 'Este formulário deve ser usado dentro do contexto adequado',
      icon: 'error',
      confirmButtonText: 'OK'
    });
  };

  return {
    currentStep,
    totalSteps,
    errors,
    isLoading,
    isSubmitting,
    handleSubmit,
  };
};